/**
 * Estado da corrida da Batalha Infinita.
 *
 * Vive fora de `useRocketanks` de propósito: aquele store já tem 3.600 linhas e
 * carrega seis modos de jogo. Tudo que é exclusivo do solo infinito — semente
 * da corrida, contrato da onda, cartas escolhidas, recordes — fica aqui, e o
 * store de jogo apenas emite eventos.
 *
 * Persistido: só o que sobrevive à morte (recordes, medalhas, totais). O estado
 * da corrida em si é transiente e é zerado a cada `beginRun`.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

import {
  applyContractEvent,
  rollWaveContract,
  type ContractEvent,
  type EnemyKind,
  type WaveContract,
} from "@/lib/infinite/contracts";
import { drawCardHand, modifierDeltasFor, type RunCard } from "@/lib/infinite/cards";
import {
  aggregateModifiers,
  NEUTRAL_MODIFIERS,
  type RunModifiers,
} from "@/lib/infinite/runModifiers";
import {
  getWorldModifier,
  medalsFor,
  type WorldModifier,
} from "@/lib/infinite/milestones";
import { createRunSeed, deterministicSeed } from "@/lib/infinite/rng";
import type { ArenaArchetype, HazardType } from "@shared/arenaTerrain";

/** Quantos arquétipos recentes o sorteio evita repetir. */
const ARCHETYPE_MEMORY = 4;

export interface WaveIntroInfo {
  wave: number;
  archetype: ArenaArchetype;
  hazards: HazardType[];
  worldModifier: WorldModifier;
  isBoss: boolean;
  isElite: boolean;
}

interface InfiniteRunState {
  // ---------- Persistido ----------
  /** Maior onda já alcançada em qualquer corrida. */
  bestWave: number;
  /** Maior pontuação de uma corrida. */
  bestScore: number;
  /** Corridas concluídas (morte ou saída). */
  totalRuns: number;
  /** Contratos cumpridos ao longo de todas as corridas. */
  contractsCompleted: number;

  // ---------- Corrida atual (transiente) ----------
  runActive: boolean;
  runSeed: number;
  chosenCardIds: string[];
  modifiers: RunModifiers;
  contract: WaveContract | null;
  waveStartedAt: number;
  archetypeHistory: ArenaArchetype[];
  currentArchetype: ArenaArchetype;
  currentHazards: HazardType[];
  /** Bônus de chips acumulado por contratos cumpridos nesta corrida. */
  contractChips: number;
  /** Contratos cumpridos nesta corrida. */
  runContractsCompleted: number;

  // ---------- Draft ----------
  draftOpen: boolean;
  draftHand: RunCard[];
  draftWave: number;
  rerollsLeft: number;

  // ---------- Abertura de onda ----------
  intro: WaveIntroInfo | null;

  /** Recorde batido nesta corrida (para a celebração, uma vez só). */
  recordCelebrated: boolean;

  // ---------- Ações ----------
  beginRun: (seed?: number) => void;
  endRun: (waveReached: number, score: number) => { newRecord: boolean };
  startWave: (wave: number, availableKinds: readonly EnemyKind[]) => void;
  setWaveScene: (archetype: ArenaArchetype, hazards: HazardType[]) => void;
  pushContractEvent: (event: ContractEvent) => void;
  openDraft: (wave: number) => void;
  chooseCard: (cardId: string) => void;
  rerollDraft: () => void;
  closeDraft: () => void;
  showIntro: (info: WaveIntroInfo) => void;
  dismissIntro: () => void;
  worldModifierFor: (wave: number) => WorldModifier;
  registerWaveReached: (wave: number) => boolean;
}

const emptyRun = {
  runActive: false,
  runSeed: 0,
  chosenCardIds: [] as string[],
  modifiers: { ...NEUTRAL_MODIFIERS } as RunModifiers,
  contract: null as WaveContract | null,
  waveStartedAt: 0,
  archetypeHistory: [] as ArenaArchetype[],
  currentArchetype: "open" as ArenaArchetype,
  currentHazards: [] as HazardType[],
  contractChips: 0,
  runContractsCompleted: 0,
  draftOpen: false,
  draftHand: [] as RunCard[],
  draftWave: 0,
  rerollsLeft: 0,
  intro: null as WaveIntroInfo | null,
  recordCelebrated: false,
};

export const useInfiniteRun = create<InfiniteRunState>()(
  persist(
    (set, get) => ({
      bestWave: 0,
      bestScore: 0,
      totalRuns: 0,
      contractsCompleted: 0,

      ...emptyRun,

      beginRun: (seed) => {
        set({
          ...emptyRun,
          runActive: true,
          runSeed: seed ?? createRunSeed(),
          modifiers: { ...NEUTRAL_MODIFIERS },
        });
      },

      endRun: (waveReached, score) => {
        const state = get();
        const newRecord = waveReached > state.bestWave;
        set({
          ...emptyRun,
          bestWave: Math.max(state.bestWave, waveReached),
          bestScore: Math.max(state.bestScore, score),
          totalRuns: state.totalRuns + (state.runActive ? 1 : 0),
        });
        return { newRecord };
      },

      startWave: (wave, availableKinds) => {
        const { runSeed } = get();
        const contract = rollWaveContract(
          wave,
          availableKinds,
          deterministicSeed("contract", runSeed, wave),
        );
        set({ contract, waveStartedAt: Date.now() });
      },

      setWaveScene: (archetype, hazards) => {
        set((state) => ({
          currentArchetype: archetype,
          currentHazards: hazards,
          archetypeHistory: [archetype, ...state.archetypeHistory].slice(0, ARCHETYPE_MEMORY),
        }));
      },

      pushContractEvent: (event) => {
        const { contract } = get();
        if (!contract || contract.status !== "active") return;

        const next = applyContractEvent(contract, event);
        if (next === contract) return;

        if (next.status === "completed") {
          set((state) => ({
            contract: next,
            contractChips: state.contractChips + next.reward,
            runContractsCompleted: state.runContractsCompleted + 1,
            contractsCompleted: state.contractsCompleted + 1,
          }));
          return;
        }
        set({ contract: next });
      },

      openDraft: (wave) => {
        const { runSeed, chosenCardIds, modifiers } = get();
        const hand = drawCardHand(
          wave,
          chosenCardIds,
          deterministicSeed("draft", runSeed, wave),
        );
        // Sem cartas elegíveis (corrida muito longa, tudo já escolhido): o
        // draft simplesmente não abre e a onda avança normalmente.
        if (hand.length === 0) {
          set({ draftOpen: false, draftHand: [], draftWave: wave });
          return;
        }
        set({
          draftOpen: true,
          draftHand: hand,
          draftWave: wave,
          rerollsLeft: 1 + modifiers.extraRerolls,
        });
      },

      chooseCard: (cardId) => {
        const { chosenCardIds, draftHand } = get();
        if (!draftHand.some((card) => card.id === cardId)) return;

        const nextIds = [...chosenCardIds, cardId];
        set({
          chosenCardIds: nextIds,
          modifiers: aggregateModifiers(modifierDeltasFor(nextIds)),
          draftOpen: false,
          draftHand: [],
        });
      },

      rerollDraft: () => {
        const { rerollsLeft, draftWave, runSeed, chosenCardIds, draftHand } = get();
        if (rerollsLeft <= 0) return;

        const used = 1 + (1 + get().modifiers.extraRerolls - rerollsLeft);
        const hand = drawCardHand(
          draftWave,
          chosenCardIds,
          deterministicSeed("draft", runSeed, draftWave, "reroll", used),
        );
        set({
          draftHand: hand.length > 0 ? hand : draftHand,
          rerollsLeft: rerollsLeft - 1,
        });
      },

      closeDraft: () => set({ draftOpen: false, draftHand: [] }),

      showIntro: (info) => set({ intro: info }),
      dismissIntro: () => set({ intro: null }),

      worldModifierFor: (wave) => getWorldModifier(wave, get().runSeed),

      /**
       * Registra a onda alcançada e devolve `true` na primeira vez que a
       * corrida ultrapassa o recorde — para a celebração acender uma vez só.
       */
      registerWaveReached: (wave) => {
        const state = get();
        if (wave <= state.bestWave || state.recordCelebrated) {
          if (wave > state.bestWave) set({ bestWave: wave });
          return false;
        }
        set({ bestWave: wave, recordCelebrated: true });
        return true;
      },
    }),
    {
      name: "rocketanks-infinite-run",
      version: 1,
      // Só o que sobrevive à corrida: o resto é reconstruído a cada partida.
      partialize: (state) => ({
        bestWave: state.bestWave,
        bestScore: state.bestScore,
        totalRuns: state.totalRuns,
        contractsCompleted: state.contractsCompleted,
      }),
    },
  ),
);

/** Medalhas já conquistadas, derivadas do recorde. */
export function useEarnedMedals() {
  const bestWave = useInfiniteRun((s) => s.bestWave);
  return medalsFor(bestWave);
}
